import React from "react";
import { TextField } from "@mui/material";
import {
  WEBHOOK_AUTH_TYPE_OPTIONS,
  WebhookAuthField,
  WebhookAuthValues,
  WebhookFieldErrors,
  authBodyJsonError,
  authTypeLabel,
  isBearer,
  isMaskedSecret,
  normalizeAuthType,
} from "../utils/webhookAuth";

export const AUTH_TYPE_HELP =
  "How the webhook authenticates. Basic and Digest read the credentials from Auth Body; Bearer posts Auth Body to the Auth Endpoint to fetch a token. Leave unset to send unauthenticated.";
/** One-liner for tight table cells; the full sentence rides the tooltip. */
export const AUTH_TYPE_HELP_SHORT = "Leave unset to send unauthenticated.";
export const AUTH_ENDPOINT_HELP =
  "Authentication endpoint URL for bearer token requests.";
export const AUTH_TOKEN_PATH_HELP =
  "JSON path to extract bearer token from auth response (e.g. 'token', 'access_token', 'data.token').";
export const AUTH_BODY_BASIC_HELP =
  'The credentials, as JSON: {"username": "...", "password": "..."}.';
export const AUTH_BODY_BEARER_HELP =
  "JSON body posted to the Auth Endpoint to request a token.";
export const MASKED_SECRET_HELP =
  "Saved value hidden. Type to replace it; leave it to keep the current value.";

/**
 * The four webhook auth fields (ENG-1737), shared by the site-webhook forms.
 * Controlled: the parent owns the values and decides what to send.
 */
export const WebhookAuthFields = ({
  values,
  onFieldChange,
  errors = {},
  width = 350,
  compact = false,
}: {
  values: WebhookAuthValues;
  onFieldChange: (field: WebhookAuthField, value: string | null) => void;
  errors?: WebhookFieldErrors;
  width?: number;
  // Short helper text (full sentence as a tooltip) for table cells.
  compact?: boolean;
}) => {
  const authType = normalizeAuthType(values.auth_type);
  const bearer = isBearer(authType);
  const bodyMasked = isMaskedSecret(values.auth_body);
  const tokenPathMasked = isMaskedSecret(values.auth_token_path);
  const bodyError = errors.auth_body || authBodyJsonError(values.auth_body);

  // Focusing a masked secret selects it, so typing replaces the dots.
  const selectIfMasked = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isMaskedSecret(event.target.value)) {
      event.target.select();
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="webhook-auth-fields">
      <TextField
        select
        SelectProps={{ native: true }}
        label="Auth Type"
        variant="standard"
        value={authType}
        onChange={(event) => {
          const nextValue = event.target.value;
          onFieldChange("auth_type", nextValue || null);
        }}
        error={Boolean(errors.auth_type)}
        helperText={
          errors.auth_type || (compact ? AUTH_TYPE_HELP_SHORT : AUTH_TYPE_HELP)
        }
        title={compact ? AUTH_TYPE_HELP : undefined}
        InputLabelProps={{ shrink: true }}
        inputProps={{ "aria-label": "Auth Type" }}
        sx={{ fontFamily: "Mulish", width }}
      >
        {WEBHOOK_AUTH_TYPE_OPTIONS.map((option) => (
          <option key={option.value || "none"} value={option.value}>
            {option.label}
          </option>
        ))}
      </TextField>

      {bearer && (
        <TextField
          label="Auth Endpoint"
          variant="standard"
          type="url"
          placeholder="https://host/api/v2/login"
          value={values.auth_endpoint ?? ""}
          onChange={(event) => {
            const nextValue = event.target.value;
            onFieldChange("auth_endpoint", nextValue);
          }}
          error={Boolean(errors.auth_endpoint)}
          helperText={errors.auth_endpoint || AUTH_ENDPOINT_HELP}
          inputProps={{ "aria-label": "Auth Endpoint" }}
          sx={{ fontFamily: "Mulish", width }}
        />
      )}

      {authType && (
        <TextField
          label="Auth Body"
          variant="outlined"
          multiline
          rows={4}
          autoComplete="off"
          value={values.auth_body ?? ""}
          onFocus={selectIfMasked}
          onChange={(event) => {
            const nextValue = event.target.value;
            onFieldChange("auth_body", nextValue);
          }}
          error={Boolean(bodyError)}
          helperText={
            bodyError ||
            (bodyMasked
              ? MASKED_SECRET_HELP
              : bearer
              ? AUTH_BODY_BEARER_HELP
              : AUTH_BODY_BASIC_HELP)
          }
          inputProps={{ "aria-label": "Auth Body", spellCheck: false }}
          sx={{ fontFamily: "Mulish", width }}
        />
      )}

      {bearer && (
        <TextField
          label="Auth Token Path"
          variant="standard"
          autoComplete="off"
          value={values.auth_token_path ?? ""}
          onFocus={selectIfMasked}
          onChange={(event) => {
            const nextValue = event.target.value;
            onFieldChange("auth_token_path", nextValue);
          }}
          error={Boolean(errors.auth_token_path)}
          helperText={
            errors.auth_token_path ||
            (tokenPathMasked ? MASKED_SECRET_HELP : AUTH_TOKEN_PATH_HELP)
          }
          inputProps={{ "aria-label": "Auth Token Path" }}
          sx={{ fontFamily: "Mulish", width }}
        />
      )}
    </div>
  );
};

/** Read-only summary of a saved webhook's auth, for the view tab. */
export const WebhookAuthSummary = ({ webhook }: { webhook: any }) => {
  const authType = normalizeAuthType(webhook?.auth_type);
  return (
    <div className="flex flex-col gap-1" data-testid="webhook-auth-summary">
      <span>{authTypeLabel(webhook?.auth_type)}</span>
      {isBearer(authType) && (
        <>
          <span>Endpoint: {webhook?.auth_endpoint || "—"}</span>
          <span>Token path: {webhook?.auth_token_path || "—"}</span>
        </>
      )}
      {authType && webhook?.auth_body && (
        <span className="whitespace-pre-wrap break-all">
          Body: {webhook.auth_body}
        </span>
      )}
    </div>
  );
};
